import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/lib/auth'
import { LoginPage } from '@/features/auth/LoginPage'
import { AppShell } from '@/features/shell/AppShell'
import { HomePage } from '@/features/shell/HomePage'
import { RootRedirect } from '@/features/shell/RootRedirect'
import { RequireModule } from '@/features/shell/RequireModule'
import { CarreirasPage } from '@/features/carreiras'
import {
  PlanoContratacaoPage,
  RecrutamentoPage,
  VagaDetailPage,
} from '@/features/recrutamento'
import {
  AdmissaoPage,
  AssinaturaPage,
  BeneficiosPage,
  DpPage,
  ExperienciaPage,
  FeriasPage,
  HistoricoPage,
  MassaPage,
  RescisaoPage,
  RotinaPage,
} from '@/features/dp'
import {
  AniversariantesPage,
  CargoFormPage,
  CargosPage,
  CentralIntegracaoPage,
  ColaboradorDetailPage,
  ColaboradorFormPage,
  CompetenciasPage,
  DnaPage,
  EmpresasPage,
  ImportarBitrixPage,
  ImportarCargosPage,
  ImportarColaboradoresPage,
  ImportarEmpresasPage,
  ImportarRhidPage,
  MesclarDuplicadosPage,
  OrganogramaPage,
  RhPage,
  TimelinePage,
} from '@/features/rh'
import { GestorPage } from '@/features/gestor'
import { PortalPage } from '@/features/portal'
import { AnalyticsPage } from '@/features/analytics'
import {
  AlertasPage,
  CommandCenterPage,
  DocumentosPage,
  IaRhPage,
  IndicadoresPage,
  IntegracoesPage,
  KnowledgeBasePage,
  OnboardingShadowPage,
  ReunioesPage,
  StubModulePage,
  WorkflowsPage,
} from '@/features/ops'
import { ClimaPage, RedePage, TvPage, WhatsappPage } from '@/features/comunicacao'
import { UsuariosPage } from '@/features/admin'
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function stub(title: string, matrixKey: string, subtitle?: string) {
  return (
    <RequireModule matrixKey={matrixKey}>
      <StubModulePage title={title} matrixKey={matrixKey} subtitle={subtitle} />
    </RequireModule>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/carreiras" element={<CarreirasPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/app" element={<AppShell />}>
              <Route
                index
                element={
                  <RequireModule matrixKey="dashboard">
                    <HomePage />
                  </RequireModule>
                }
              />
              <Route
                path="command-center"
                element={
                  <RequireModule matrixKey="command-center">
                    <CommandCenterPage />
                  </RequireModule>
                }
              />
              <Route
                path="alertas"
                element={
                  <RequireModule matrixKey="alertas-corporativos">
                    <AlertasPage />
                  </RequireModule>
                }
              />
              <Route path="relatorios" element={stub('Relatórios', 'relatorios')} />
              <Route
                path="recrutamento"
                element={
                  <RequireModule matrixKey="recrutamento">
                    <RecrutamentoPage />
                  </RequireModule>
                }
              />
              <Route
                path="recrutamento/:vagaId"
                element={
                  <RequireModule matrixKey="recrutamento">
                    <VagaDetailPage />
                  </RequireModule>
                }
              />
              <Route
                path="plano-contratacao"
                element={
                  <RequireModule matrixKey="plano-contratacao">
                    <PlanoContratacaoPage />
                  </RequireModule>
                }
              />
              <Route
                path="rh"
                element={
                  <RequireModule matrixKey="colaboradores">
                    <RhPage />
                  </RequireModule>
                }
              />
              <Route
                path="rh/novo"
                element={
                  <RequireModule matrixKey="colaboradores">
                    <ColaboradorFormPage />
                  </RequireModule>
                }
              />
              <Route
                path="rh/importar"
                element={
                  <RequireModule matrixKey="colaboradores">
                    <ImportarColaboradoresPage />
                  </RequireModule>
                }
              />
              <Route
                path="rh/importar/rhid"
                element={
                  <RequireModule matrixKey="colaboradores">
                    <ImportarRhidPage />
                  </RequireModule>
                }
              />
              <Route
                path="rh/importar/bitrix"
                element={
                  <RequireModule matrixKey="colaboradores">
                    <ImportarBitrixPage />
                  </RequireModule>
                }
              />
              <Route
                path="rh/importar/duplicados"
                element={
                  <RequireModule matrixKey="colaboradores">
                    <MesclarDuplicadosPage />
                  </RequireModule>
                }
              />
              <Route
                path="rh/:colaboradorId/editar"
                element={
                  <RequireModule matrixKey="colaboradores">
                    <ColaboradorFormPage />
                  </RequireModule>
                }
              />
              <Route
                path="rh/:colaboradorId"
                element={
                  <RequireModule matrixKey="colaboradores">
                    <ColaboradorDetailPage />
                  </RequireModule>
                }
              />
              <Route
                path="timeline"
                element={
                  <RequireModule matrixKey="timeline">
                    <TimelinePage />
                  </RequireModule>
                }
              />
              <Route
                path="perfil-dna"
                element={
                  <RequireModule matrixKey="perfil-dna">
                    <DnaPage />
                  </RequireModule>
                }
              />
              <Route
                path="aniversariantes"
                element={
                  <RequireModule matrixKey="aniversariantes">
                    <AniversariantesPage />
                  </RequireModule>
                }
              />
              <Route
                path="cargos"
                element={
                  <RequireModule matrixKey="cargos">
                    <CargosPage />
                  </RequireModule>
                }
              />
              <Route
                path="cargos/importar"
                element={
                  <RequireModule matrixKey="cargos">
                    <ImportarCargosPage />
                  </RequireModule>
                }
              />
              <Route
                path="cargos/novo"
                element={
                  <RequireModule matrixKey="cargos">
                    <CargoFormPage />
                  </RequireModule>
                }
              />
              <Route
                path="cargos/:id"
                element={
                  <RequireModule matrixKey="cargos">
                    <CargoFormPage />
                  </RequireModule>
                }
              />
              <Route
                path="empresas"
                element={
                  <RequireModule matrixKey="empresas">
                    <EmpresasPage />
                  </RequireModule>
                }
              />
              <Route
                path="empresas/importar"
                element={
                  <RequireModule matrixKey="empresas">
                    <ImportarEmpresasPage />
                  </RequireModule>
                }
              />
              <Route
                path="organograma"
                element={
                  <RequireModule matrixKey="organograma-vivo">
                    <OrganogramaPage />
                  </RequireModule>
                }
              />
              <Route
                path="competencias"
                element={
                  <RequireModule matrixKey="competencias">
                    <CompetenciasPage />
                  </RequireModule>
                }
              />
              <Route
                path="central-integracao"
                element={
                  <RequireModule matrixKey="central-integracao">
                    <CentralIntegracaoPage />
                  </RequireModule>
                }
              />
              <Route
                path="treinamentos"
                element={stub(
                  'Treinamentos',
                  'desenvolvimento',
                  'T&D e trilhas de desenvolvimento. Catálogo e matrículas avançadas no legado.',
                )}
              />
              <Route
                path="td/avd"
                element={stub(
                  'Avaliação de Desempenho',
                  'desenvolvimento',
                  'Ciclo AVD trimestral: autoavaliação, gestor, consenso e PDI. Em construção na SPA.',
                )}
              />
              <Route
                path="td/feedback"
                element={stub(
                  'Avaliação de Feedback',
                  'desenvolvimento',
                  'Feedback mensal do gestor com cada membro da equipe. Em construção na SPA.',
                )}
              />
              <Route
                path="td/pdi"
                element={stub(
                  'Programa de Desenvolvimento Individual',
                  'desenvolvimento',
                  'PDI vinculado ao consenso da AVD e à Hi Academy. Em construção na SPA.',
                )}
              />
              <Route
                path="dp/admissao"
                element={
                  <RequireModule matrixKey="dp-admissao">
                    <AdmissaoPage />
                  </RequireModule>
                }
              />
              <Route
                path="dp/assinatura"
                element={
                  <RequireModule matrixKey="assinatura-digital">
                    <AssinaturaPage />
                  </RequireModule>
                }
              />
              <Route
                path="dp/historico"
                element={
                  <RequireModule matrixKey="timeline">
                    <HistoricoPage />
                  </RequireModule>
                }
              />
              <Route
                path="clima/acoes"
                element={stub(
                  'Ações Internas',
                  'vibracao-corporativa',
                  'Ações de clima e cultura a partir das pesquisas.',
                )}
              />
              <Route
                path="sst/cipa"
                element={stub(
                  'CIPA',
                  'nr01',
                  'Comissão Interna de Prevenção de Acidentes — Saúde e Segurança.',
                )}
              />
              <Route
                path="sst/ergonomia"
                element={stub(
                  'Ergonomia',
                  'laudo-tecnico',
                  'Avaliações e laudos ergonômicos — Saúde e Segurança.',
                )}
              />
              <Route
                path="documentos"
                element={
                  <RequireModule matrixKey="documentos">
                    <DocumentosPage />
                  </RequireModule>
                }
              />
              <Route path="ouvidoria" element={stub('Ouvidoria', 'ouvidoria')} />
              <Route
                path="knowledge-base"
                element={
                  <RequireModule matrixKey="knowledge-base">
                    <KnowledgeBasePage />
                  </RequireModule>
                }
              />
              <Route
                path="dp"
                element={
                  <RequireModule matrixKey="departamento">
                    <DpPage />
                  </RequireModule>
                }
              />
              <Route
                path="dp/ferias"
                element={
                  <RequireModule matrixKey="dp-ferias">
                    <FeriasPage />
                  </RequireModule>
                }
              />
              <Route
                path="dp/rescisao"
                element={
                  <RequireModule matrixKey="dp-rescisao">
                    <RescisaoPage />
                  </RequireModule>
                }
              />
              <Route
                path="dp/beneficios"
                element={
                  <RequireModule matrixKey="dp-beneficios">
                    <BeneficiosPage />
                  </RequireModule>
                }
              />
              <Route
                path="dp/rotina"
                element={
                  <RequireModule matrixKey="dpc">
                    <RotinaPage />
                  </RequireModule>
                }
              />
              <Route
                path="dp/experiencia"
                element={
                  <RequireModule matrixKey="dp-avaliacao-exp">
                    <ExperienciaPage />
                  </RequireModule>
                }
              />
              <Route
                path="dp/nr01"
                element={stub(
                  'NR-01',
                  'nr01',
                  'Gestão de riscos psicossociais (NR-01). Avaliações e planos no legado.',
                )}
              />
              <Route
                path="dp/risco"
                element={stub(
                  'Risco trabalhista',
                  'risco-trabalhista',
                  'Mapa de risco trabalhista. Análise e mitigações no legado.',
                )}
              />
              <Route
                path="dp/epi"
                element={stub(
                  'EPI e fardamento',
                  'epi-fardamento',
                  'Controle de EPI e fardamento. Entregas e estoque no legado.',
                )}
              />
              <Route
                path="dp/laudos"
                element={stub(
                  'Laudos técnicos',
                  'laudo-tecnico',
                  'Laudos e documentos técnicos. Emissão e templates no legado.',
                )}
              />
              <Route
                path="dp/massa"
                element={
                  <RequireModule matrixKey="dp-massa">
                    <MassaPage />
                  </RequireModule>
                }
              />
              <Route
                path="gestor"
                element={
                  <RequireModule matrixKey="gestor">
                    <GestorPage />
                  </RequireModule>
                }
              />
              <Route
                path="reunioes"
                element={
                  <RequireModule matrixKey="reunioes-lideranca">
                    <ReunioesPage />
                  </RequireModule>
                }
              />
              <Route
                path="calendario"
                element={stub('Calendário corporativo', 'calendario-corporativo')}
              />
              <Route
                path="indicadores"
                element={
                  <RequireModule matrixKey="indicadores">
                    <IndicadoresPage />
                  </RequireModule>
                }
              />
              <Route
                path="ia-rh"
                element={
                  <RequireModule matrixKey="ia-rh">
                    <IaRhPage />
                  </RequireModule>
                }
              />
              <Route path="automacao" element={stub('Automações', 'automacao')} />
              <Route
                path="onboarding-shadow"
                element={
                  <RequireModule matrixKey="onboarding-shadow">
                    <OnboardingShadowPage />
                  </RequireModule>
                }
              />
              <Route
                path="workflows"
                element={
                  <RequireModule matrixKey="workflows">
                    <WorkflowsPage />
                  </RequireModule>
                }
              />
              <Route
                path="colaborador"
                element={
                  <RequireModule matrixKey="portal">
                    <PortalPage />
                  </RequireModule>
                }
              />
              <Route
                path="analytics"
                element={
                  <RequireModule matrixKey="analytics">
                    <AnalyticsPage />
                  </RequireModule>
                }
              />
              <Route
                path="usuarios"
                element={
                  <RequireModule matrixKey="usuarios">
                    <UsuariosPage />
                  </RequireModule>
                }
              />
              <Route
                path="integracoes"
                element={
                  <RequireModule matrixKey="integracoes">
                    <IntegracoesPage />
                  </RequireModule>
                }
              />
              <Route
                path="rede"
                element={
                  <RequireModule matrixKey="rede-feed">
                    <RedePage />
                  </RequireModule>
                }
              />
              <Route
                path="tv"
                element={
                  <RequireModule matrixKey="tv-corporativa">
                    <TvPage />
                  </RequireModule>
                }
              />
              <Route
                path="clima"
                element={
                  <RequireModule matrixKey="vibracao-corporativa">
                    <ClimaPage />
                  </RequireModule>
                }
              />
              <Route
                path="whatsapp"
                element={
                  <RequireModule matrixKey="whatsapp-inbox">
                    <WhatsappPage />
                  </RequireModule>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/carreiras" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}
